import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpToLine,
  ChevronDown,
  Clapperboard,
  Clock,
  ExternalLink,
  Link2,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import {
  publishContent,
  deletePost,
  SocialConnection,
  ContentPublishStatus,
  PLATFORM_INFO,
} from '../../api/ayrshareApi';
import { useAyrshareConnections } from '../../contexts/AyrshareConnectionContext';
import { usePublishStatus } from '../../contexts/PublishStatusContext';
import { useQueueStatus } from '../../contexts/QueueStatusContext';
import { carouselApi, tendApi, PUBLISHED_EVENT, QUEUE_RESUMED_EVENT } from '../../api/tendApi';
import { TakeFields, announceTakeChange, pickTakeFields } from '../../utils/takeState';
import { useFollowAnchor } from '../../hooks/useFollowAnchor';
import PublishResultToast from './PublishResultToast';
import { useAyrshareConnect } from '../../hooks/useAyrshareConnect';
import { legAllowsPostActions, legFace } from './publishLegState';
import { useCadenceUpdating } from '../../utils/cadenceUpdating';
import { DropdownPosition, dropdownWrapperStyle, positionDropdown } from '../../utils/dropdownPosition';
import {
  enqueueContent,
  dequeueContent,
  holdContent,
  resumeContent,
  QueueTicket, QueueItemStatus } from '../../api/tendQueueApi';

/**
 * The unified Smart Publishing button — ONE control for the whole publish
 * lifecycle, mounted everywhere content can publish (TendV2ContentCard,
 * the generation-results modal, platform previews).
 *
 * Six states (per the smart_publishing_button_states mockup):
 *   Tended      "Queue"            → queue back / queue front / publish now
 *   Queued      "Queued · ≈2:40PM" → move to front / hold / publish now / remove
 *   Held        "Held"             → resume / publish now / remove
 *   Publishing  "Publishing…"      → locked, no menu
 *   Published   "Published"        → view post / delete post
 *   Failed      "Needs attention"  → auth: reconnect / other: try again + publish now; + remove
 *
 * State sources: PublishStatusContext (brand-scoped published truth) beats
 * QueueStatusContext ticket status beats "no ticket" (tended). Queueing
 * needs no connection; Publish now does (row disables with the reason).
 *
 * Take (phase 1): every face before Published also carries "Request a
 * Take" / "Remove take request" (the host lifts the flag via
 * takeRequested / onTakeChange; the card header's take glyph shows it).
 * Take precedes Queued: requesting on a queued/held/failed item pulls it
 * out of its line (the face drops to Queue), and queueing a take item
 * clears the request — the two are consecutive stages, never both.
 */

interface SmartPublishButtonProps {
  brandId: number;
  contentId: number;
  targetPlatform: string; // The platform this content was created for
  onPublishSuccess?: (result: any) => void;
  onPublishError?: (error: string) => void;
  disabled?: boolean;
  disabledReason?: string; // Reason why publishing is disabled (shown as tooltip)
  className?: string;
  compact?: boolean; // Smaller version for inline use
  /** Optional lifted queued state (TendV2ContentCard's purple header). */
  queued?: boolean | null;
  onQueuedChange?: (queued: boolean) => void;
  /** Take phase 1: whether the item carries a take request. Omitted =
      the host doesn't know, and the take rows are not offered. */
  takeRequested?: boolean;
  /** The take fields after a request / removal (host lifts them; the
      take-changed window event reaches every other view of the item). */
  onTakeChange?: (take: TakeFields) => void;
}

// Normalize platform name (handle variations)
// Map variations like "youtube_shorts" -> "youtube", "instagram_reels" -> "instagram"
export const normalizePlatform = (platform: string): string => {
  const normalized = platform?.toLowerCase() || '';
  if (normalized === 'twitter/x' || normalized === 'x') return 'twitter';
  if (normalized.includes('_')) {
    const basePlatform = normalized.split('_')[0];
    const platformMappings: Record<string, string> = {
      youtube: 'youtube',
      instagram: 'instagram',
      tiktok: 'tiktok',
      facebook: 'facebook',
      linkedin: 'linkedin',
      twitter: 'twitter',
      pinterest: 'pinterest',
      reddit: 'reddit',
      bluesky: 'bluesky',
      threads: 'threads',
    };
    return platformMappings[basePlatform] || basePlatform;
  }
  return normalized;
};

type ButtonState = 'loading' | 'tended' | 'queued' | 'held' | 'publishing' | 'published' | 'deleted' | 'failed';

/** Whole hours until a UTC iso stamp, as "23 hrs" / "1 hr" (never below 1). */
const hoursLeft = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return undefined;
  const h = Math.max(1, Math.ceil(ms / 3600000));
  return `${h} hr${h === 1 ? '' : 's'}`;
};

/** A UTC iso stamp as a short browser-local moment ("Oct 2, 3:15 PM"). */
const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Published-face sub-label: local time-of-day with zone if it went out
 * today ("12:54 PM PDT" — the zone label disambiguates for anyone reading
 * across timezones), otherwise the short date ("Aug 14", with a 2-digit
 * year once it crosses years — the list-row recipe). Correct only when the
 * API timestamp carries a zone suffix (backends append 'Z' to naive-UTC). */
export const formatPublishedSub = (publishedAt?: string | null): string | undefined => {
  if (!publishedAt) return undefined;
  const date = new Date(publishedAt);
  if (isNaN(date.getTime())) return undefined;
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString([], {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  }
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(sameYear ? {} : { year: '2-digit' }),
  });
};

interface MenuRow {
  icon: React.ReactNode;
  label: string;
  desc: string;
  onClick?: () => void;
  href?: string;
  kind?: 'normal' | 'elevated' | 'danger' | 'fix';
  disabled?: boolean;
  disabledDesc?: string;
}

const MENU_WIDTH = 320;
/** Per-row estimate (label + description) — picks the side before the menu has rendered, never a cap. */
const MENU_ROW_ESTIMATE = 60;

const SmartPublishButton: React.FC<SmartPublishButtonProps> = ({
  brandId,
  contentId,
  targetPlatform,
  onPublishSuccess,
  onPublishError,
  disabled = false,
  disabledReason,
  className = '',
  compact = false,
  queued: liftedQueued,
  onQueuedChange,
  takeRequested,
  onTakeChange,
}) => {
  const { token } = useAuth();
  const { getConnections, refreshConnections, subscribe: subscribeConnections } = useAyrshareConnections();
  const { getPublishStatus, refreshPublishStatus } = usePublishStatus();
  const { getQueueStatus, setKnownStatus, subscribe: subscribeQueue, refreshStatuses, refreshAll } = useQueueStatus();

  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [publishStatus, setPublishStatus] = useState<ContentPublishStatus | null>(null);
  const [ticket, setTicket] = useState<{
    queued: boolean;
    status?: string;
    scheduledAt?: string | null;
    projectedAt?: string | null;
    lineReason?: string | null;
    lineNextOpenAt?: string | null;
    lastError?: string | null;
    errorKind?: 'auth' | 'not_linked' | 'account' | 'too_long' | 'rejected' | 'unknown' | 'rate_limited' | 'transient' | 'other' | null;
    holdReason?: string | null;
    position?: number;
    rank?: number | null;
    isFront?: boolean;
  } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<DropdownPosition | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState<{ status: string; message?: string } | null>(null);
  // Carousel pre-publish warnings awaiting user confirmation (warn, never block)
  const [pendingWarnings, setPendingWarnings] = useState<string[] | null>(null);
  // What the shared queue bus knew before this pill flipped it to
  // "publishing", so a failed publish can put it back (a queued ticket
  // stays queued if the publish fails).
  const priorKnownRef = useRef<QueueItemStatus | null>(null);

  const buttonRef = useRef<HTMLDivElement>(null);

  const normalized = normalizePlatform(targetPlatform);
  const platformInfo = PLATFORM_INFO[normalized] || { name: targetPlatform, icon: '📱', color: '#6B7280' };
  // A cadence save is re-planning this line — the fire time is about to move
  const cadenceUpdating = useCadenceUpdating(brandId, normalized);

  // ---- data hydration -----------------------------------------------------

  const hydrateTicket = useCallback(async () => {
    const s = await getQueueStatus(contentId);
    setTicket({
      queued: !!s.queued,
      status: s.status,
      scheduledAt: s.scheduled_at,
      projectedAt: s.projected_at,
      lineReason: s.line_reason,
      lineNextOpenAt: s.line_next_open_at ?? null,
      lastError: s.last_error,
      errorKind: s.error_kind,
      holdReason: s.hold_reason ?? null,
      position: s.position,
      rank: s.rank,
      isFront: s.is_front,
    });
    if (onQueuedChange && liftedQueued === null) onQueuedChange(!!s.queued);
  }, [contentId, getQueueStatus, onQueuedChange, liftedQueued]);

  useEffect(() => {
    let mounted = true;
    getConnections(brandId)
      .then((all) => { if (mounted) setConnections(all); })
      .finally(() => { if (mounted) setLoadingConnections(false); });
    getPublishStatus(brandId, contentId).then((s) => { if (mounted && s) setPublishStatus(s); });
    hydrateTicket();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, contentId]);

  // An edit that brought this post back under its channel's character
  // ceiling lifted the system hold server-side (PATCH → queue_resumed):
  // re-read the ticket so the face flips Held → Queued without a reload.
  useEffect(() => {
    const onResumed = (e: Event) => {
      if ((e as CustomEvent).detail?.contentId === contentId) refreshStatuses([contentId]);
    };
    window.addEventListener(QUEUE_RESUMED_EVENT, onResumed);
    return () => window.removeEventListener(QUEUE_RESUMED_EVENT, onResumed);
  }, [contentId, refreshStatuses]);

  // Another pill for this item published it (Publish now in the modal while
  // this row sits in the list behind it): read the fresh shared status so
  // this face flips to Published too, instead of offering Queue / Publish
  // for a post that is already live (item 4888).
  useEffect(() => {
    const onPublished = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (!d || d.contentId !== contentId || d.brandId !== brandId) return;
      getPublishStatus(brandId, contentId).then((s) => { if (s) setPublishStatus(s); }).catch(() => {});
    };
    window.addEventListener(PUBLISHED_EVENT, onPublished);
    return () => window.removeEventListener(PUBLISHED_EVENT, onPublished);
  }, [brandId, contentId, getPublishStatus]);

  // Connections change elsewhere — another card's Connect, the tab coming
  // back after an Ayrshare-dashboard change — reach every button.
  useEffect(() => subscribeConnections((bid, list) => { if (bid === brandId) setConnections(list); }),
    [subscribeConnections, brandId]);

  // Cross-card queue mutations (another card's move-to-front, a strip drag
  // reorder) shift this ticket's rank — track the shared status bus.
  useEffect(() => {
    return subscribeQueue((id, s) => {
      if (id !== contentId) return;
      setTicket({
        queued: !!s.queued,
        status: s.status,
        scheduledAt: s.scheduled_at,
        projectedAt: s.projected_at,
        lineReason: s.line_reason,
        lineNextOpenAt: s.line_next_open_at ?? null,
        lastError: s.last_error,
        errorKind: s.error_kind,
        holdReason: s.hold_reason ?? null,
        position: s.position,
        rank: s.rank,
        isFront: s.is_front,
      });
      if (onQueuedChange) onQueuedChange(!!s.queued);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, subscribeQueue]);

  // The background scheduler mutates tickets with no push — re-hydrate
  // periodically while ticketed so ≈ times / publishing / failed track reality.
  useEffect(() => {
    if (!ticket?.queued) return;
    const interval = setInterval(() => {
      if (!document.hidden) hydrateTicket();
    }, 90000);
    return () => clearInterval(interval);
  }, [ticket?.queued, hydrateTicket]);

  // Clear transient result banner
  const clearResult = useCallback(() => setResult(null), []);
  useEffect(() => {
    if (result) {
      const timer = setTimeout(clearResult, 3000);
      return () => clearTimeout(timer);
    }
  }, [result, clearResult]);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    // Delay so the opening click doesn't immediately close it
    const id = window.setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { window.clearTimeout(id); document.removeEventListener('mousedown', close); };
  }, [menuOpen]);

  const connection = connections.find((c) => c.platform === normalized);
  // "Connected" means linked AND healthy: a linked account whose token is
  // dead (needs_reconnect from the backend's connection health) can't publish
  // either — it needs Reconnect, not Connect.
  const needsReconnect = !!connection?.needs_reconnect;
  const isConnected = !!connection?.is_connected && !needsReconnect;
  // The ticket is waiting on the channel: held by the system for a relink
  // (Ayrshare said the account isn't linked), or its line is closed for it.
  const waitingOnConnection =
    ticket?.holdReason === 'not_connected' || ticket?.lineReason === 'not_connected';
  const targetPlatformStatus = publishStatus?.platforms?.[normalized];
  const isPublished = !!(publishStatus?.published && targetPlatformStatus?.status === 'success');
  const publishedUrl = targetPlatformStatus?.url;
  // How far the network took the leg (backend-decided): a post accepted
  // with no post id yet is "sent", not live — the face says so and the
  // post actions wait for the confirmation (TikTok Cooldown Trigger ticket).
  const legState = targetPlatformStatus?.leg_state ?? null;

  const state: ButtonState = loadingConnections && !ticket
    ? 'loading'
    : isPublished && publishStatus?.deleted
    // Dead: stays published (it went out), offers nothing — no delete, no
    // publish, no queue. The backend refuses all three as well.
    ? 'deleted'
    : isPublished
    ? 'published'
    : publishing || ticket?.status === 'publishing'
    ? 'publishing'
    : ticket?.queued && ticket.status === 'held'
    ? 'held'
    : ticket?.queued && ticket.status === 'failed'
    ? 'failed'
    : ticket?.queued
    ? 'queued'
    : 'tended';

  // ---- actions ------------------------------------------------------------

  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (err) {
      console.error('Queue action failed:', err);
      setResult({ status: 'error', message: err instanceof Error ? err.message : 'Action failed' });
    } finally { setBusy(false); setMenuOpen(false); }
  };

  const syncKnown = (t: any) => {
    setKnownStatus(contentId, {
      queued: true, position: t.position, platform: t.platform,
      is_front: t.is_front, is_back: t.is_back,
      status: t.status, scheduled_at: t.scheduled_at ?? null, last_error: t.last_error ?? null,
      projected_at: t.projected_at ?? null, line_reason: t.line_reason ?? null,
      line_open_now: t.line_open_now, line_next_open_at: t.line_next_open_at ?? null,
      rank: t.rank,
    });
  };

  // The server decides the ticket's state on every door: an over-limit
  // post comes back from enqueue/resume already HELD (hold_reason
  // over_limit) — trust the response's status, never assume 'queued'.
  const ticketFromResponse = (t: QueueTicket) => ({
    queued: true,
    status: t.status || 'queued',
    position: t.position, rank: t.rank, isFront: t.is_front,
    projectedAt: t.projected_at, lineReason: t.line_reason,
    lineNextOpenAt: t.line_next_open_at ?? null,
    holdReason: t.hold_reason ?? null,
    lastError: t.last_error ?? null,
  });

  const ticketAsKnown = (): QueueItemStatus => (ticket?.queued
    ? {
        queued: true, status: ticket.status, scheduled_at: ticket.scheduledAt,
        projected_at: ticket.projectedAt, line_reason: ticket.lineReason,
        line_next_open_at: ticket.lineNextOpenAt ?? null,
        last_error: ticket.lastError, error_kind: ticket.errorKind ?? null,
        hold_reason: ticket.holdReason ?? null, position: ticket.position,
        rank: ticket.rank ?? null, is_front: ticket.isFront,
      }
    : { queued: false });

  // Publish now flips the face to "Publishing…" on the click itself — on
  // THIS pill and on every other mounted pill for the item (the list row
  // behind the modal, the strip card) via the shared queue-status bus.
  // Item 4888: the face showed nothing for the seconds the pre-check and
  // the Ayrshare call took, and a second pill still offered "Queue to
  // front" — which queued a post that was already live.
  const beginPublishingFace = () => {
    setPublishing(true);
    if (priorKnownRef.current === null) {
      priorKnownRef.current = ticketAsKnown();
      setKnownStatus(contentId, { ...priorKnownRef.current, status: 'publishing' });
    }
  };
  const endPublishingFace = (published: boolean) => {
    setPublishing(false);
    const prior = priorKnownRef.current;
    priorKnownRef.current = null;
    if (published) setKnownStatus(contentId, { queued: false });
    else if (prior) setKnownStatus(contentId, prior);
  };
  // Tell the other mounted pills the post is live: they re-read the shared
  // publish-status cache, which this pill has just refreshed.
  const announcePublished = () => {
    window.dispatchEvent(new CustomEvent(PUBLISHED_EVENT, { detail: { brandId, contentId } }));
  };

  // Every door that changes a line moves the NEIGHBOURS' times too: a hold
  // hands its time to the next card, Move to front takes it from the old
  // first, Remove passes it on. This pill only knows its own ticket, so
  // re-read every pill on the page through the shared bus (only changed
  // ones announce) — otherwise the next card showed the old time until its
  // 60 s cache ran out or a reload (staging QA, 2026-10-27).
  const refreshNeighbours = () => { refreshAll().catch(() => { /* next poll heals */ }); };

  const doEnqueue = (placement: 'back' | 'front') => withBusy(async () => {
    const t = await enqueueContent(contentId, brandId, placement);
    setTicket(ticketFromResponse(t));
    syncKnown(t);
    onQueuedChange?.(true);
    // Queueing a take IS the take being done — the server cleared the
    // request; tell every view of the item (glyph, list tag, Take strip).
    if (t.take_cleared || takeRequested) {
      const cleared = pickTakeFields(null);
      onTakeChange?.(cleared);
      announceTakeChange(contentId, cleared);
    }
    refreshNeighbours();
  });

  const doHold = () => withBusy(async () => {
    const t = await holdContent(contentId);
    setTicket({ ...ticketFromResponse(t), status: 'held' });
    syncKnown(t);
    refreshNeighbours();
  });

  const doResume = () => withBusy(async () => {
    const t = await resumeContent(contentId);
    setTicket(ticketFromResponse(t));
    syncKnown(t);
    refreshNeighbours();
  });

  const doRemove = () => withBusy(async () => {
    await dequeueContent(contentId);
    setTicket({ queued: false });
    setKnownStatus(contentId, { queued: false });
    onQueuedChange?.(false);
    refreshNeighbours();
  });

  const startPublish = async () => {
    setMenuOpen(false);
    beginPublishingFace();
    // Carousel pre-publish check: surface platform-limit warnings and let the
    // user confirm. Fails open — validation errors never block publishing.
    try {
      const validation = await carouselApi.validateCarouselForPublish(contentId, [normalized]);
      if (validation.is_carousel && validation.warnings && validation.warnings.length > 0) {
        // The person decides first; the face returns to what it was until
        // they confirm (doPublish flips it again).
        endPublishingFace(false);
        setPendingWarnings(validation.warnings);
        return;
      }
    } catch (err) {
      console.warn('Carousel pre-publish validation unavailable, proceeding:', err);
    }
    await doPublish();
  };

  // Publishing spends the ticket (matches the old PublishNowButton flow)
  const spendTicket = async () => {
    if (!ticket?.queued) return;
    try { await dequeueContent(contentId); } catch { /* non-fatal */ }
    setTicket({ queued: false });
    setKnownStatus(contentId, { queued: false });
    onQueuedChange?.(false);
    refreshNeighbours();
  };

  /** After a gateway-severed publish request the truth lives in the
   * publish-status record (written when Ayrshare answers the SERVER, which
   * keeps going after nginx hangs up on the browser). Poll it for ~2.5min
   * before believing the error. */
  const waitForPublishRecord = async (): Promise<ContentPublishStatus | null> => {
    for (let attempt = 0; attempt < 15; attempt++) {
      if (attempt > 0) await new Promise((r) => setTimeout(r, 10000));
      try {
        const s = await refreshPublishStatus(brandId, contentId);
        if (s?.published) return s;
      } catch { /* transient — keep polling */ }
    }
    return null;
  };

  const doPublish = async () => {
    setPendingWarnings(null);
    beginPublishingFace();
    setResult(null);
    let published = false;
    try {
      const response = await publishContent(brandId, contentId, [normalized], token || undefined);
      if (response.status !== 'success') throw new Error(response.error || 'Publishing failed');
      published = true;
      await spendTicket();
      // The history row is committed before the publish call returns, so
      // the fresh status is readable at once — and the face goes straight
      // from Publishing… to Published, with no "tended" flash between.
      const s = await refreshPublishStatus(brandId, contentId).catch(() => null);
      if (s) setPublishStatus(s);
      setResult({ status: 'success', message: 'Published!' });
      onPublishSuccess?.(response);
      announcePublished();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish';
      if ((err as any)?.maybeStillPublishing) {
        const confirmed = await waitForPublishRecord();
        if (confirmed) {
          published = true;
          await spendTicket();
          setPublishStatus(confirmed);
          setResult({ status: 'success', message: 'Published!' });
          onPublishSuccess?.(confirmed);
          announcePublished();
          return;
        }
      }
      setResult({ status: 'error', message });
      onPublishError?.(message);
    } finally {
      endPublishingFace(published);
    }
  };

  // manual = "Mark as deleted" for networks with no API delete: nothing is
  // removed remotely, it records that the user already did that in the app.
  const handleDelete = async (manual = false) => {
    if (!publishStatus?.ayrshare_id) return;
    if (publishStatus.delete_mode !== (manual ? 'manual' : 'api')) return;
    const question = manual
      ? `Mark this post as deleted? Only do this after removing it on ${platformInfo.name}. It stays in Published, marked deleted, and can't be published again.`
      : `Delete this post from ${platformInfo.name}? It stays in Published, marked deleted, and can't be published again.`;
    if (!window.confirm(question)) return;
    setMenuOpen(false);
    setDeleting(true);
    try {
      const response = await deletePost(brandId, publishStatus.ayrshare_id, token || undefined, { manual });
      if (response.status !== 'success') throw new Error(response.error || 'Failed to delete post');
      // Still published — now dead. (Used to null the status, which dropped
      // the item back to a publishable "tended" face.)
      setPublishStatus({
        ...publishStatus,
        deleted: true,
        deleted_at: response.deleted_at || new Date().toISOString(),
        deleted_via: response.deleted_via || (manual ? 'manual' : 'api'),
        delete_mode: null,
        can_delete: false,
      });
      setResult({ status: 'success', message: manual ? 'Marked as deleted' : 'Post deleted' });
      refreshPublishStatus(brandId, contentId);
    } catch (err) {
      setResult({ status: 'error', message: err instanceof Error ? err.message : 'Failed to delete' });
    } finally {
      setDeleting(false);
    }
  };

  // The shared connect flow: opens Ayrshare's linking page, settles when
  // the popup closes OR this tab comes back (mobile), polls ~20 s for the
  // channel to read linked, and reports the outcome. On a link, the
  // channel's held tickets resume server-side — re-read ours.
  const onConnectionsRefreshed = useCallback((list: SocialConnection[]) => {
    setConnections(list);
    refreshStatuses([contentId]);
  }, [contentId, refreshStatuses]);
  const { connect: connectChannel, connecting, outcome: connectOutcome } =
    useAyrshareConnect(brandId, token || undefined, onConnectionsRefreshed);
  const handleConnect = () => {
    setMenuOpen(false);
    connectChannel(normalized);
  };
  // Say what happened for a few seconds on the pill, then let the face
  // return to its state (which, on a link, is no longer "not connected").
  const [outcomeNote, setOutcomeNote] = useState<string | null>(null);
  useEffect(() => {
    if (!connectOutcome) return;
    setOutcomeNote(connectOutcome.linked ? `${platformInfo.name} linked ✓` : `didn't link — try again`);
    const id = window.setTimeout(() => setOutcomeNote(null), 8000);
    refreshConnections(brandId).then(setConnections).catch(() => {});
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOutcome]);

  // ---- menu rows per state ------------------------------------------------

  const publishNowRow: MenuRow = {
    icon: <Send className="w-4 h-4" />,
    label: 'Publish now',
    desc: 'Publishes immediately, ignoring your schedule.',
    onClick: startPublish,
    kind: 'elevated',
    disabled: disabled || !isConnected,
    disabledDesc: disabled
      ? (disabledReason || 'Publishing unavailable for this item.')
      : `${platformInfo.name} isn't connected — connect it to publish.`,
  };
  const connectRow: MenuRow | null = (!isConnected || waitingOnConnection) ? {
    icon: connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />,
    label: `${needsReconnect || waitingOnConnection ? 'Reconnect' : 'Connect'} ${platformInfo.name}`,
    desc: waitingOnConnection || ticket?.queued
      ? `Opens the social-account connection window. Queued posts resume on their own once ${platformInfo.name} is linked.`
      : 'Opens the social-account connection window.',
    onClick: handleConnect,
    kind: 'fix',
  } : null;
  const removeRow: MenuRow = {
    icon: <X className="w-4 h-4" />,
    label: 'Remove from queue',
    desc: "Takes this out of the queue. It won't publish.",
    onClick: doRemove,
    kind: 'danger',
  };

  // Take (phase 1): ask for / withdraw a to-camera recording. Offered on
  // every face before Published; a queued item keeps its ticket and simply
  // gains the take. The server is idempotent and refuses published items.
  const doRequestTake = () => withBusy(async () => {
    const take = await tendApi.requestTake(contentId, brandId);
    if (take.dequeued) {
      // Out of the line (Take precedes Queued): the face drops to Queue;
      // the channel strip keeps the card in place until refresh
      // (left_for: 'take'), the Take strip picks it up.
      priorKnownRef.current = null;
      setTicket({ queued: false });
      setKnownStatus(contentId, { queued: false, left_for: 'take' });
      onQueuedChange?.(false);
      refreshNeighbours();
    }
    onTakeChange?.(take);
    announceTakeChange(contentId, take);
    setResult({ status: 'success', message: take.dequeued ? 'Take requested · out of the queue' : 'Take requested' });
  });
  const doRemoveTake = () => withBusy(async () => {
    const take = await tendApi.removeTakeRequest(contentId);
    onTakeChange?.(take);
    announceTakeChange(contentId, take);
    setResult({ status: 'success', message: 'Take request removed' });
  });
  const takeRow: MenuRow | null = takeRequested === undefined ? null : takeRequested
    ? {
        icon: <Clapperboard className="w-4 h-4" />,
        label: 'Remove take request',
        desc: 'Drops it from the Take queue without queueing it. The script and any recording stay with the item.',
        onClick: doRemoveTake,
      }
    : {
        icon: <Clapperboard className="w-4 h-4" />,
        label: 'Request a Take',
        desc: ticket?.queued
          ? 'Asks for a to-camera recording with the teleprompter. Pulls it out of this channel\'s line into the Take queue; queue it again once recorded.'
          : 'Asks for a to-camera recording with the teleprompter. It joins the Take queue on the Original page.',
        onClick: doRequestTake,
      };
  const takeRows = takeRow ? [takeRow] : [];

  // Over the channel's character ceiling: queueing stays open (posts get
  // trimmed right up to the slot), but the row says what the queue will do —
  // hold at fire time, never send, resume once it's edited under.
  const overLimitNote = disabled && disabledReason
    ? ` Over the limit now (${disabledReason.replace(/^Post exceeds /, '')}) — the queue holds it at publish time until it's trimmed.`
    : '';
  let rows: MenuRow[] = [];
  if (state === 'tended') {
    rows = [
      { icon: <ArrowDownToLine className="w-4 h-4" />, label: 'Queue to back', desc: `Adds to the end of the queue. Best for evergreen content.${overLimitNote}`, onClick: () => doEnqueue('back') },
      { icon: <ArrowUpToLine className="w-4 h-4" />, label: 'Queue to front', desc: `Publishes in the next open slot. Best for timely posts.${overLimitNote}`, onClick: () => doEnqueue('front') },
      publishNowRow,
      ...(connectRow ? [connectRow] : []),
      ...takeRows,
    ];
  } else if (state === 'queued') {
    rows = [
      ...(connectRow ? [connectRow] : []),
      ...(!ticket?.isFront ? [{ icon: <ArrowUpToLine className="w-4 h-4" />, label: 'Move to front', desc: 'Jumps to the next open slot.', onClick: () => doEnqueue('front') } as MenuRow] : []),
      { icon: <Pause className="w-4 h-4" />, label: 'Hold', desc: 'Pauses this post in place. The queue flows around it.', onClick: doHold },
      publishNowRow,
      ...takeRows,
      removeRow,
    ];
  } else if (state === 'held') {
    const systemHeld = ticket?.holdReason === 'over_limit' || ticket?.holdReason === 'paced';
    rows = ticket?.holdReason === 'not_connected'
      ? [
          // Held for the relink: Resume would just hold again — the fix IS
          // connecting, and the ticket resumes by itself when that lands.
          ...(connectRow ? [connectRow] : []),
          ...takeRows,
          removeRow,
        ]
      : [
      {
        icon: <Play className="w-4 h-4" />, label: 'Resume',
        desc: ticket?.holdReason === 'paced'
          ? `${ticket?.lastError || 'Paced by the channel'} It returns to the line on its own at that time; resuming early can hold it again.`
          : systemHeld
          ? `${ticket?.lastError || 'Over the channel limit'}. Edit it under the limit and it resumes on its own; resuming as-is just holds again at publish time.`
          : 'Returns this to the queue in its place.',
        onClick: doResume,
      },
      ...(connectRow ? [connectRow] : []),
      publishNowRow,
      ...takeRows,
      removeRow,
    ];
  } else if (state === 'failed') {
    const connectionFailure = ticket?.errorKind === 'auth' || ticket?.errorKind === 'not_linked'
      || ticket?.errorKind === 'account' || !isConnected;
    rows = connectionFailure
      ? [
          {
            icon: <RefreshCw className="w-4 h-4" />,
            label: `${ticket?.errorKind === 'not_linked' && !needsReconnect ? 'Connect' : 'Reconnect'} ${platformInfo.name}`,
            desc: ticket?.errorKind === 'not_linked'
              ? `${platformInfo.name} isn't linked. Connect it and this post goes back in line on its own.`
              : ticket?.errorKind === 'account'
              ? `${platformInfo.name} flagged the account itself (a restriction, verification or permissions). Sort that out on ${platformInfo.name}, then reconnect and this post goes back in line.`
              : "This channel's connection expired. Reconnect it and this post goes back in line on its own.",
            onClick: handleConnect, kind: 'fix',
          },
          ...takeRows,
          removeRow,
        ]
      : [
          { icon: <RefreshCw className="w-4 h-4" />, label: 'Try again', desc: 'Clears the failure and re-queues for the next open slot.', onClick: () => doEnqueue('back') },
          publishNowRow,
          ...takeRows,
          removeRow,
        ];
  } else if (state === 'published' && !legAllowsPostActions(legState)) {
    // Sent but not confirmed by the network: nothing to view, nothing to
    // delete yet. The face carries the state; the menu stays empty.
    rows = [];
  } else if (state === 'published') {
    rows = [
      ...(publishedUrl ? [{ icon: <ExternalLink className="w-4 h-4" />, label: 'View post', desc: `View the live post on ${platformInfo.name}.`, href: publishedUrl } as MenuRow] : []),
      ...(publishStatus?.delete_mode === 'api' ? [{
        icon: deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />,
        label: 'Delete post',
        desc: `Removes the live post from ${platformInfo.name}. It stays in Published, marked deleted.`,
        onClick: () => handleDelete(false),
        kind: 'danger',
      } as MenuRow] : []),
      // No API delete on this network (Instagram/TikTok — the backend
      // decides): an honest pair instead of a Delete that cannot work.
      ...(publishStatus?.delete_mode === 'manual' ? [
        ...(publishedUrl ? [{
          icon: <ExternalLink className="w-4 h-4" />,
          label: `Delete on ${platformInfo.name}`,
          desc: `${platformInfo.name} doesn't allow deleting from TEND. Opens the post so you can remove it there.`,
          href: publishedUrl,
        } as MenuRow] : []),
        {
          icon: deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />,
          label: 'Mark as deleted',
          desc: `Already removed it on ${platformInfo.name}? Record that here.`,
          onClick: () => handleDelete(true),
          kind: 'danger',
        } as MenuRow,
      ] : []),
    ];
  }
  // state === 'deleted' → no rows: a dead post offers nothing.

  // ---- button face per state (TEND language: signal dot + glass pill;
  // solid white commit face for the one primary action, Queue) ------------

  // Real slot (scheduler-assigned, same-day) or read-time projection — the
  // projection walks the cadence windows forward so weekend/closed-window
  // queues still show WHEN instead of a bare "#N in line".
  const formatFireTime = (iso: string): string => {
    const d = new Date(iso);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return time;
    const tomorrow = new Date(now.getTime() + 86400000);
    if (d.toDateString() === tomorrow.toDateString()) return `tmrw ${time}`;
    if (d.getTime() - now.getTime() < 6 * 86400000) {
      return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };
  const fireIso = ticket?.scheduledAt || ticket?.projectedAt || null;
  const fireTime = fireIso ? formatFireTime(fireIso) : null;

  const face: { label: string; sub?: string; dot: string; dotGlow?: string; icon?: React.ReactNode; tint?: React.CSSProperties } = (() => {
    switch (state) {
      case 'loading':
        return { label: 'Loading…', dot: 'rgba(255,255,255,.3)', icon: <Loader2 className="w-3.5 h-3.5 animate-spin" /> };
      case 'tended':
        return { label: 'Queue', dot: 'rgba(255,255,255,.4)' };
      case 'queued':
        if (ticket?.lineReason === 'relink_hold') {
          // The channel was just (re)linked: a paced line waits a while
          // before the next post, Publish now included — it reopens itself.
          return {
            label: `Held · ${platformInfo.name} just reconnected`,
            sub: ticket.lineNextOpenAt ? `resumes ${fmtWhen(ticket.lineNextOpenAt)}` : 'resumes on its own',
            dot: '#fbbf24', dotGlow: '0 0 8px rgba(251,191,36,.7)',
            icon: <Clock className="w-3.5 h-3.5" />,
            tint: { boxShadow: 'inset 0 0 0 1px rgba(251,191,36,.45)' },
          };
        }
        if (ticket?.lineReason === 'cooling_off') {
          // A spam verdict (or the manual switch) paused the line. The
          // fewest words: "Cooling" and the hours left (Shawn, 2026-09-30).
          return {
            label: 'Cooling', sub: hoursLeft(ticket.lineNextOpenAt),
            dot: '#7dd3fc', dotGlow: '0 0 8px rgba(125,211,252,.7)',
            icon: <Clock className="w-3.5 h-3.5" />,
          };
        }
        // Fire time when scheduled, else the queue position. Auto-off is
        // already conveyed by the channel's Auto toggle in the section
        // header, so the queued face doesn't repeat it.
        return {
          label: 'Queued',
          // A line closed for its connection says so instead of a fire time
          // that can't come — the Connect row in the menu is the fix.
          sub: ticket?.lineReason === 'not_connected'
            ? `connect ${platformInfo.name}`
            : cadenceUpdating ? 'Updating…'
            : fireTime ? `≈ ${fireTime}` : ticket?.rank != null ? `#${ticket.rank} in line` : undefined,
          dot: 'var(--signal-scribe)', dotGlow: '0 0 8px rgba(178,140,255,.7)',
          icon: <Clock className="w-3.5 h-3.5" />,
        };
      case 'held':
        // A person's hold is calm grey; a SYSTEM hold is amber with the fix
        // in the sub-line — over_limit resumes once the text is edited
        // under; not_connected resumes once the channel is (re)linked.
        if (ticket?.holdReason === 'not_connected') {
          return {
            label: `Held · connect ${platformInfo.name}`, sub: 'resumes when linked',
            dot: '#fbbf24', dotGlow: '0 0 8px rgba(251,191,36,.7)',
            icon: <Link2 className="w-3.5 h-3.5" />,
            tint: { boxShadow: 'inset 0 0 0 1px rgba(251,191,36,.45)' },
          };
        }
        if (ticket?.holdReason === 'paced') {
          // The channel's pacing rule (one attempt a day, a gap between
          // posts) holds it with a clock — it returns to the line by itself.
          return {
            label: `Held · ${platformInfo.name} pacing`,
            sub: ticket.scheduledAt ? `resumes ${fmtWhen(ticket.scheduledAt)}` : 'resumes on its own',
            dot: '#fbbf24', dotGlow: '0 0 8px rgba(251,191,36,.7)',
            icon: <Clock className="w-3.5 h-3.5" />,
            tint: { boxShadow: 'inset 0 0 0 1px rgba(251,191,36,.45)' },
          };
        }
        if (ticket?.holdReason === 'over_limit') {
          return {
            label: 'Held · over limit', sub: 'edit to resume',
            dot: '#fbbf24', dotGlow: '0 0 8px rgba(251,191,36,.7)',
            icon: <AlertTriangle className="w-3.5 h-3.5" />,
            tint: { boxShadow: 'inset 0 0 0 1px rgba(251,191,36,.45)' },
          };
        }
        return { label: 'Held', dot: 'rgba(255,255,255,.4)', icon: <Pause className="w-3.5 h-3.5" /> };
      case 'publishing':
        return { label: 'Publishing…', dot: 'var(--signal-feed)', dotGlow: '0 0 8px rgba(255,162,62,.7)', icon: <Loader2 className="w-3.5 h-3.5 animate-spin" /> };
      case 'published': {
        const leg = legFace(legState, platformInfo.name);
        if (!leg.postActions) {
          // Accepted by the network, not confirmed: "Sent · awaiting TikTok",
          // then "Unconfirmed · check TikTok" after a day.
          return {
            label: leg.label, sub: leg.sub,
            dot: '#fbbf24', dotGlow: '0 0 8px rgba(251,191,36,.7)',
            icon: <Clock className="w-3.5 h-3.5" />,
          };
        }
        return {
          label: 'Published',
          sub: formatPublishedSub(publishStatus?.published_at),
          dot: 'var(--signal-live)', dotGlow: '0 0 8px rgba(68,211,106,.7)',
        };
      }
      case 'deleted':
        return {
          label: 'Deleted',
          sub: formatPublishedSub(publishStatus?.deleted_at),
          dot: 'rgba(255,255,255,.3)',
          icon: <Trash2 className="w-3.5 h-3.5" />,
          tint: { opacity: .7 },
        };
      case 'failed':
        return {
          label: 'Needs attention', dot: 'var(--signal-rec)', dotGlow: '0 0 8px rgba(218,41,28,.7)',
          icon: <AlertTriangle className="w-3.5 h-3.5" />,
          tint: { boxShadow: 'inset 0 0 0 1px rgba(255,141,126,.45)' },
        };
    }
  })();

  // Center the menu under the trigger (the pill sits centered in the card
  // header), clamped to the viewport; flips ABOVE when there's no room —
  // anchored by its bottom edge to the pill's top (positionDropdown), so
  // the flipped menu hugs the pill whatever its height. The old
  // `rect.top - 348` assumed a 340px menu and left a three-row menu
  // floating ~150px up, on the card above. Natural height (measured once
  // rendered, estimated from the rows before that) only picks the side.
  const rowCount = rows.length;
  const placeMenu = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    // scrollHeight of the inner menu = its uncapped natural height (the
    // wrapper's offsetHeight is already clamped to maxHeight)
    const natural = (menuRef.current?.firstElementChild as HTMLElement | null)?.scrollHeight
      || rowCount * MENU_ROW_ESTIMATE + 16;
    setMenuPosition(positionDropdown(rect, MENU_WIDTH, natural, { align: 'center' }));
  }, [rowCount]);

  // Follow the pill while it's visible; close when it slides under the
  // Tended page's sticky chrome or off-screen (see useFollowAnchor).
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useFollowAnchor(menuOpen, buttonRef, placeMenu, closeMenu, menuRef);

  const openMenu = () => {
    if (state === 'publishing' || state === 'loading' || rows.length === 0) return;
    if (!menuOpen) placeMenu();
    setMenuOpen((o) => !o);
  };

  return (
    <div ref={buttonRef} className={`relative inline-block ${className}`}>
      <button
        onClick={openMenu}
        disabled={state === 'publishing' || state === 'loading' || busy}
        className="tend-btn tend-btn--ink"
        style={{ padding: '7px 13px', fontSize: 13, gap: 7, ...(face.tint || {}) }}
        title={
          state === 'failed' ? (ticket?.lastError || 'Publish failed')
          : state === 'deleted' ? `This post was deleted${publishStatus?.deleted_via === 'manual' ? ` on ${platformInfo.name}` : ''} — a deleted item can't be published again`
          : state === 'publishing' ? 'Sending to the platform — actions unlock when it resolves'
          : undefined
        }
      >
        <span
          className="rounded-full flex-shrink-0"
          style={{ width: 6, height: 6, background: face.dot, boxShadow: face.dotGlow }}
        />
        {face.icon}
        {!compact && <span>{face.label}</span>}
        {!compact && (outcomeNote || face.sub) && (
          <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4 }}>
            {outcomeNote || face.sub}
          </span>
        )}
        {rows.length > 0 && state !== 'publishing' && (
          <ChevronDown
            className="w-3.5 h-3.5"
            style={{ opacity: .6, transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform .25s var(--ease-settle)' }}
          />
        )}
      </button>

      {/* Outcome line, portaled: inside the header it painted under the card
          body (the chrome is its own stacking context) — see PublishResultToast. */}
      <PublishResultToast anchorRef={buttonRef} result={result} onClear={clearResult} />

      {menuOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          // Above the legacy TEND/TendedContent modals (zIndex 999999 with an
          // inner 1000001 layer) — the menu portals to body, so it must clear
          // the tallest stacking context it can be triggered from. The
          // wrapper carries top OR bottom (side) + maxHeight; the menu inside
          // shrinks to it and scrolls rather than overflow the viewport.
          style={{ ...dropdownWrapperStyle(menuPosition, Z.MENU), width: MENU_WIDTH }}
          onMouseDown={(e) => e.stopPropagation()}
        >
        <div className="tend-menu tend-ink" style={{ minHeight: 0, overflowY: 'auto' }}>
          {state === 'failed' && ticket?.lastError && (
            <div
              className="px-3 py-2.5 text-xs leading-relaxed"
              style={{
                borderRadius: 12,
                background: 'rgba(218,41,28,.12)',
                boxShadow: 'inset 0 0 0 1px rgba(255,141,126,.35)',
                color: 'var(--text-2)',
              }}
            >
              {ticket.errorKind === 'not_linked'
                ? `${platformInfo.name} isn't linked to this brand — the post couldn't be sent.`
                : ticket.errorKind === 'auth'
                ? `${platformInfo.name}'s connection has expired — the post couldn't be sent.`
                : ticket.errorKind === 'account'
                ? `${platformInfo.name} needs attention on the account itself — ${ticket.lastError}`
                : ticket.errorKind === 'rejected'
                ? `${platformInfo.name} rejected this post and would again — fix it, then publish now. ${ticket.lastError}`
                : ticket.errorKind === 'unknown'
                ? `${platformInfo.name} said no twice without a reason — publish now to try again. ${ticket.lastError}`
                : ticket.lastError}
            </div>
          )}
          {state === 'held' && ticket?.holdReason === 'not_connected' && (
            <div
              className="px-3 py-2.5 text-xs leading-relaxed"
              style={{
                borderRadius: 12,
                background: 'rgba(251,191,36,.12)',
                boxShadow: 'inset 0 0 0 1px rgba(251,191,36,.35)',
                color: 'var(--text-2)',
              }}
            >
              Waiting for {platformInfo.name} — it isn't linked. Connect it and this post goes back in line on its own.
            </div>
          )}
          {rows.map((row, i) => {
            const semantic =
              row.kind === 'danger' ? 'var(--signal-rec-soft)'
              : row.kind === 'fix' ? 'var(--signal-feed)'
              : row.kind === 'elevated' ? 'var(--accent-blue-soft)'
              : undefined;
            const inner = (
              <>
                <span
                  className="mt-0.5 flex-shrink-0 flex"
                  style={{ color: semantic || 'var(--text-3)', opacity: row.disabled ? .45 : 1 }}
                >
                  {row.icon}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block"
                    style={{
                      fontSize: 13, fontWeight: 600,
                      color: row.disabled ? 'var(--text-3)' : semantic || 'var(--text)',
                    }}
                  >
                    {row.label}
                  </span>
                  <span className="tend-micro block" style={{ textTransform: 'none', letterSpacing: 0.4, marginTop: 1, lineHeight: 1.45 }}>
                    {row.disabled ? row.disabledDesc || row.desc : row.desc}
                  </span>
                </span>
              </>
            );
            // Destructive tail separated by the house hairline divider
            const dividerBefore = row.kind === 'danger' && i > 0 && rows[i - 1].kind !== 'danger';
            const rowStyle: React.CSSProperties = {
              width: '100%',
              alignItems: 'flex-start',
              ...(row.disabled ? { cursor: 'not-allowed', opacity: .8 } : {}),
              ...(dividerBefore ? { borderTop: '1px solid rgba(255,255,255,.12)', borderRadius: '0 0 12px 12px', marginTop: 3, paddingTop: 11 } : {}),
            };
            return row.href && !row.disabled ? (
              <a key={row.label} href={row.href} target="_blank" rel="noopener noreferrer" className="tend-menu-item" style={rowStyle} onClick={() => setMenuOpen(false)}>
                {inner}
              </a>
            ) : (
              <button key={row.label} type="button" disabled={row.disabled || busy} onClick={row.onClick} className="tend-menu-item" style={rowStyle}>
                {inner}
              </button>
            );
          })}
        </div>
        </div>,
        document.body
      )}

      {pendingWarnings && createPortal(
        <div className="tend-modal-veil" style={{ zIndex: Z.WARNINGS }} onClick={() => setPendingWarnings(null)}>
          <div className="tend-ink tend-modal-card" style={{ maxWidth: 440, width: 'calc(100vw - 32px)', padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--signal-feed)' }} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Before you publish</h3>
            </div>
            <ul className="text-sm space-y-1.5 mb-4 list-disc pl-5" style={{ color: 'var(--text-2)' }}>
              {pendingWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            <div className="flex justify-end gap-2">
              <button className="tend-btn tend-btn--glass" style={{ padding: '7px 14px', fontSize: 13 }} onClick={() => setPendingWarnings(null)}>
                Cancel
              </button>
              <button className="tend-btn tend-btn--cta" style={{ padding: '7px 16px', fontSize: 13 }} onClick={doPublish}>
                Publish anyway
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SmartPublishButton;
